import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Dumbbell, Flame, Loader2, Zap, StretchHorizontal, CalendarDays } from 'lucide-react';
import { useLanguage } from '../../context/AppContext';
import { getLocalDateString } from '../../utils/dateUtils';

const WORKOUT_TYPES = [
  { value: 'musculation', labelFr: 'Musculation', labelEn: 'Weight Training', icon: Dumbbell, color: 'orange', cal: 350 },
  { value: 'cardio', labelFr: 'Cardio', labelEn: 'Cardio', icon: Zap, color: 'red', cal: 400 },
  { value: 'mobilité', labelFr: 'Mobilité', labelEn: 'Mobility', icon: StretchHorizontal, color: 'emerald', cal: 150 },
];

const DURATIONS = [15, 30, 45, 60, 90];

const WorkoutModal = ({
  open,
  onOpenChange,
  workoutForm,
  onFormChange,
  onSubmit,
  loading
}) => {
  const { language, t } = useLanguage();
  const fr = language === 'fr';

  // Iter. 406.t — Rattrapage : in-modal date picker so users can back-date
  // a workout (Tony's bug). Defaults to today; max=today (no future logs),
  // min=today-30d (matches backend RETROACTIVE_MAX_DAYS).
  const todayStr = getLocalDateString();
  const minStr = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return getLocalDateString(d); })();
  const currentDate = workoutForm.target_date || todayStr;
  const isRattrapage = currentDate !== todayStr;

  const selected = WORKOUT_TYPES.find(t => t.value === workoutForm.workout_type) || WORKOUT_TYPES[0];
  const estCal = Math.round((selected.cal / 45) * workoutForm.duration_minutes);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-sm" data-testid="workout-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight flex items-center gap-2">
            <Dumbbell className="w-6 h-6 text-orange-500" />
            {fr ? 'Log Rapide' : 'Quick Log'}
          </DialogTitle>
          <DialogDescription>
            {fr ? 'Choisis ton type et ta durée. C\'est tout.' : 'Pick your type and duration. That\'s it.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Type - Big Buttons */}
          <div className="grid grid-cols-3 gap-2">
            {WORKOUT_TYPES.map((type) => {
              const Icon = type.icon;
              const active = workoutForm.workout_type === type.value;
              return (
                <button
                  key={type.value}
                  onClick={() => onFormChange({ ...workoutForm, workout_type: type.value })}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    active
                      ? `bg-${type.color}-500/15 border-${type.color}-500 text-${type.color}-500`
                      : 'bg-secondary border-transparent hover:border-muted-foreground/30 text-muted-foreground'
                  }`}
                  data-testid={`workout-type-${type.value}`}
                >
                  <Icon className="w-7 h-7" />
                  <span className="text-xs font-bold uppercase tracking-wide">
                    {fr ? type.labelFr : type.labelEn}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Duration - Quick Pick */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              {fr ? 'Durée' : 'Duration'}
            </label>
            <div className="flex gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => onFormChange({ ...workoutForm, duration_minutes: d })}
                  className={`flex-1 py-3 rounded-lg text-center font-bold transition-all ${
                    workoutForm.duration_minutes === d
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                      : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                  }`}
                  data-testid={`workout-duration-${d}`}
                >
                  {d}<span className="text-[10px] ml-0.5">m</span>
                </button>
              ))}
            </div>
          </div>

          {/* Iter. 406.t — Rattrapage date picker. Honored by backend
              /api/workouts/log via the `target_date` field. Caps at today
              (no future) and 30 days in the past (matches retroactive policy). */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <CalendarDays className="w-4 h-4 text-orange-400" />
              {fr ? 'Date (rattrapage si nécessaire)' : 'Date (catch-up if needed)'}
            </label>
            <input
              type="date"
              value={currentDate}
              min={minStr}
              max={todayStr}
              onChange={(e) => onFormChange({ ...workoutForm, target_date: e.target.value || todayStr })}
              className="w-full h-10 px-3 text-sm rounded-lg bg-secondary border border-border text-foreground"
              data-testid="workout-date-input"
            />
            {isRattrapage && (
              <p
                className="text-[11px] text-orange-300 flex items-center gap-1"
                data-testid="workout-date-rattrapage-note"
              >
                <Flame className="w-3 h-3" />
                {fr
                  ? `Rattrapage actif : enregistré pour le ${new Date(currentDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}.`
                  : `Catch-up active: saving for ${new Date(currentDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}.`}
              </p>
            )}
          </div>

          {/* Calorie Estimate */}
          <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl text-center">
            <span className="text-muted-foreground text-sm">
              {fr ? 'Bonus estimé' : 'Est. bonus'}
            </span>
            <div className="font-heading text-3xl text-orange-500 font-black">
              +{estCal} cal
            </div>
          </div>

          {/* Submit */}
          <Button
            onClick={onSubmit}
            disabled={loading}
            className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white text-base font-bold"
            data-testid="submit-workout-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Flame className="w-5 h-5 mr-2" />
            )}
            {fr ? "C'est logué!" : "Logged!"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkoutModal;
